import { watch } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { isEqual } from 'lodash-es'
import { getCategoryDefaultFiltersFromQuery } from '@/utils/filter'

interface UseRouteQueryFilterSyncOptions {
  filterOptions: ComputedRef<any> | Ref<any>
  hasGeolocation: Ref<boolean>
  selectedFilters: ComputedRef<Record<string, unknown>>
  pagingState: { page: number }
  updateSelectedFilters: (filters: unknown) => void
  getProductList: () => void
  onBeforeSync?: (newQuery: Record<string, unknown>) => void
}

export function useRouteQueryFilterSync({
  filterOptions,
  hasGeolocation,
  selectedFilters,
  pagingState,
  updateSelectedFilters,
  getProductList,
  onBeforeSync,
}: UseRouteQueryFilterSyncOptions) {
  const nuxtApp = useNuxtApp()
  const { $store: store } = nuxtApp
  const route = useRoute()

  watch(
    () => ({ query: route.query, params: route.params }),
    ({ query: newQuery, params: newParams }, { query: oldQuery, params: oldParams }) => {
      // When route params change (e.g. subcategory switch), useAsyncData handles the full
      // filter re-sync including fetching fresh filterOptions. Skip here to avoid racing
      // against useAsyncData with stale filterOptions.
      if (!isEqual(newParams, oldParams)) return

      if (isEqual(newQuery, oldQuery)) return

      const filtersFromQuery = getCategoryDefaultFiltersFromQuery(
        newQuery as Record<string, string | unknown[]>,
        filterOptions.value,
        hasGeolocation.value
      )
      const pageFromQuery = Number(newQuery.page) || 1

      if (isEqual(filtersFromQuery, selectedFilters.value) && pageFromQuery === pagingState.page) return

      store.dispatch('filter/resetFilterState')
      onBeforeSync?.(newQuery)
      updateSelectedFilters(filtersFromQuery)
      pagingState.page = pageFromQuery
      getProductList()
    },
    { deep: true }
  )
}
